/**
 * /app/kds — Kitchen Display System: cola de preparación en tiempo real.
 *
 * Cubre TODO pedido con relevancia de cocina — salón, retiro y delivery —
 * no solo mesas: kitchenPrintCount/kitchenPrintedAt ya se usan para los 3
 * canales (ver PrintOrderDialog/print.$orderId.kitchen.tsx), así que acá se
 * lee /api/orders sin acoplarse a sesión de mesa.
 *
 * Transiciones: solo las que ya permite el dominio canónico
 * (ORDER_TRANSITIONS en sm-models.ts) vía el mismo PATCH /api/orders que usa
 * el resto del admin. "Empezar preparación" recorre el camino más corto
 * hacia EN_PREPARACION con `shortestForwardPath` — un pedido de salón entra
 * en PENDIENTE y no existe salto directo a EN_PREPARACION en el dominio, así
 * que la cola hace PENDIENTE→CONFIRMADO→EN_PREPARACION en dos PATCH en vez
 * de inventar una transición nueva.
 *
 * Gate de plan: mismo criterio real que /app/salon (dine_in_tables, tier de
 * negocio con servicio de mesa) — se sondea con /api/tables, que ya aplica
 * ese gate server-side; acá no se duplica el chequeo de plan.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useWorkspaceLocationId } from "@/lib/location-context";
import { useOperationalMode, type OperationalShortcut } from "@/hooks/use-operational-mode";
import { OperationalModeShell, OperationalModeToggle } from "@/components/operational-mode/OperationalModeShell";
import { OPERATIONAL_HOST_LAYOUT } from "@/lib/operational-mode-classes";
import { shortestForwardPath, type OrderStatus } from "@/domain/sm-models";
import { PREPARATION_QUEUE_STATUSES } from "@/domain/sm-models";
import { PrintOrderDialog } from "@/components/orders/PrintOrderDialog";
import { toast } from "sonner";
import { ChefHat, Clock, Printer, CheckCircle2, Flame } from "lucide-react";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

export const Route = createFileRoute("/app/kds")({
  component: KdsPage,
  head: () => ({ meta: [{ title: "Cocina — KDS" }] }),
});

interface KdsSelection { addonProductId: string; name: string; cantidad: number }
interface KdsItem {
  id: string; productName: string; tipoVenta: string; cantidad: number;
  unidadMedida: string; variantName: string | null; selections: KdsSelection[];
}
interface KdsOrder {
  id: string; customerName: string; notes: string | null;
  status: OrderStatus; metodEntrega: "domicilio" | "retiro"; createdAt: string;
  items: KdsItem[];
}

const QUEUE_STATUSES = PREPARATION_QUEUE_STATUSES;
const POLL_MS = 15_000;
const CLOCK_MS = 20_000;

/**
 * SCRUM-85: única "categorización" real que existe hoy para un pedido de
 * cocina — el canal de venta (mismo criterio que `kitchenChannelLabel` en
 * PrintOrderDialog.tsx). No hay noción de estación/categoría de preparación
 * en el dominio, así que el filtro operativo se arma sobre esto y no sobre
 * algo inventado.
 */
type ChannelKey = "salon" | "retiro" | "domicilio";
const CHANNEL_FILTER_STORAGE_PREFIX = "sm:kds:channelFilter:";

function channelKey(o: KdsOrder): ChannelKey {
  const notes = (o.notes ?? "").toLowerCase();
  const name = (o.customerName ?? "").toLowerCase();
  if (notes.includes("pedido mesero") || notes.includes("mesa ") || name.startsWith("mesa ")) return "salon";
  return o.metodEntrega === "domicilio" ? "domicilio" : "retiro";
}

const CHANNEL_LABELS: Record<ChannelKey, string> = { salon: "SALÓN", retiro: "RETIRO", domicilio: "DELIVERY" };
const CHANNEL_ORDER: Array<ChannelKey | "all"> = ["all", "salon", "retiro", "domicilio"];

function elapsedMinutes(createdAt: string, now: number): number {
  return Math.max(0, Math.floor((now - new Date(createdAt).getTime()) / 60000));
}

function formatElapsed(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ${mins % 60} min`;
  return `${Math.floor(hours / 24)} d ${hours % 24} h`;
}

function urgencyVariant(mins: number): "success" | "warning" | "danger" {
  if (mins >= 20) return "danger";
  if (mins >= 10) return "warning";
  return "success";
}

function KdsPage() {
  const tid = useWorkspaceTenantId();
  const contextLocationId = useWorkspaceLocationId();
  const [orders, setOrders] = useState<KdsOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  // SCRUM-87: guard sincrónico aparte de `pendingIds` — un doble-tap dispara
  // dos invocaciones de advanceOrder antes de que React llegue a re-renderizar
  // con el `pendingIds` actualizado (setState es asíncrono/batched), así que
  // leer `pendingIds.has(...)` desde el closure no alcanza para bloquear el
  // segundo click. Un ref se lee/escribe en el mismo tick, sin esa ventana.
  const pendingRef = useRef<Set<string>>(new Set());
  const [printOrderId, setPrintOrderId] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<ChannelKey | "all">("all");

  // SCRUM-85: filtro persistido — un turno entero puede pasar con el mismo
  // filtro elegido; un refresh de página a mitad de jornada no debe resetearlo.
  useEffect(() => {
    if (!tid) return;
    try {
      const stored = localStorage.getItem(`${CHANNEL_FILTER_STORAGE_PREFIX}${tid}`);
      if (stored && (CHANNEL_ORDER as string[]).includes(stored)) {
        setChannelFilter(stored as ChannelKey | "all");
      }
    } catch {
      /* storage no disponible — filtro por defecto "all" */
    }
  }, [tid]);

  const applyChannelFilter = useCallback(
    (next: ChannelKey | "all") => {
      setChannelFilter(next);
      if (!tid) return;
      try {
        localStorage.setItem(`${CHANNEL_FILTER_STORAGE_PREFIX}${tid}`, next);
      } catch {
        /* no-op */
      }
    },
    [tid],
  );

  const cycleChannelFilter = useCallback(() => {
    const idx = CHANNEL_ORDER.indexOf(channelFilter);
    applyChannelFilter(CHANNEL_ORDER[(idx + 1) % CHANNEL_ORDER.length]);
  }, [channelFilter, applyChannelFilter]);

  const refreshOrders = useCallback(async () => {
    if (!tid) return;
    try {
      // Sondea el gate real de plan (dine_in_tables) — /api/tables ya lo aplica.
      await apiGetJson(`/api/tables?action=sectors`, tid);
      const params = new URLSearchParams();
      for (const s of QUEUE_STATUSES) params.append("status", s);
        params.set("includeItems", "1");
        params.set("limit", "150");
        // SCRUM-288: tenant híbrido servicios+gastro — un turno cobrado en caja
        // no debe aparecer en la cola de cocina.
        params.set("excludeServiceBookings", "1");
        if (contextLocationId) {
          params.set("locationId", contextLocationId);
          // SCRUM-174 (cerrado 2026-08-26): backfill migración 218 llevó
          // ACTIVE_QUEUE_NULL 10 → 0; api.v1.orders ya resuelve location_id
          // default al crear. Flag includeUnassignedLocation retirado.
        }
      const data = await apiGetJson<KdsOrder[]>(`/api/orders?${params.toString()}`, tid);
      setOrders(data);
      setLock(null);
    } catch (e) {
      const planLock = (e as { planLock?: PlanApiErrorPayload }).planLock;
      if (planLock) setLock(planLock);
      // corte de red pasajero — no interrumpir el poll con toasts repetidos
    } finally {
      setLoading(false);
    }
  }, [tid, contextLocationId]);

  useEffect(() => {
    void refreshOrders();
  }, [refreshOrders]);

  useEffect(() => {
    if (!tid) return;
    const id = window.setInterval(() => void refreshOrders(), POLL_MS);
    return () => window.clearInterval(id);
  }, [tid, refreshOrders]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), CLOCK_MS);
    return () => window.clearInterval(id);
  }, []);

  const nuevas = useMemo(
    () => orders.filter((o) => o.status !== "EN_PREPARACION").sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [orders],
  );
  const enPreparacion = useMemo(
    () => orders.filter((o) => o.status === "EN_PREPARACION").sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [orders],
  );

  // SCRUM-85: el filtro de canal es puramente de presentación — nunca toca
  // `orders` (la fuente de verdad real). El shortcut de teclado y el toggle
  // touch actúan sobre exactamente esta misma vista derivada, así que ambos
  // "ven" y "bumpean" el mismo pedido.
  const visibleNuevas = useMemo(
    () => (channelFilter === "all" ? nuevas : nuevas.filter((o) => channelKey(o) === channelFilter)),
    [nuevas, channelFilter],
  );
  const visibleEnPreparacion = useMemo(
    () => (channelFilter === "all" ? enPreparacion : enPreparacion.filter((o) => channelKey(o) === channelFilter)),
    [enPreparacion, channelFilter],
  );

  const advanceOrder = useCallback(
    async (order: KdsOrder, target: OrderStatus) => {
      if (pendingRef.current.has(order.id) || !tid) return;
      const path = shortestForwardPath(order.status, target);
      if (!path || path.length === 0) return;
      pendingRef.current.add(order.id);
      setPendingIds((prev) => new Set(prev).add(order.id));
      try {
        for (const status of path) {
          await apiSendJson(`/api/orders?id=${order.id}`, "PATCH", { status }, tid);
        }
      } catch {
        toast.error("No se pudo actualizar el pedido — reintentá.");
      } finally {
        pendingRef.current.delete(order.id);
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(order.id);
          return next;
        });
        void refreshOrders();
      }
    },
    [tid, refreshOrders],
  );

  // SCRUM-85: mismo contrato que touch — F4/F8 llaman a `advanceOrder` sobre
  // el pedido más antiguo de la vista visible (mismo filtro aplicado), el
  // mismo código que ejecuta el botón de la card. No hay una segunda ruta.
  const bumpOldest = useCallback(
    (list: KdsOrder[], target: OrderStatus) => {
      const order = list[0];
      if (!order) return;
      void advanceOrder(order, target);
    },
    [advanceOrder],
  );

  const shortcuts = useMemo<OperationalShortcut[]>(
    () => [
      {
        id: "refresh",
        keys: "F2",
        label: "Actualizar ahora",
        description: "Vuelve a cargar la cola de cocina",
        handler: () => void refreshOrders(),
      },
      {
        id: "cycle-channel",
        keys: "F3",
        label: "Cambiar filtro de canal",
        description: "Todos → Salón → Retiro → Delivery",
        handler: cycleChannelFilter,
      },
      {
        id: "bump-nuevas",
        keys: "F4",
        label: "Empezar el pedido más antiguo",
        description: "Igual que tocar \"Empezar preparación\" en la primera card de Nuevas",
        handler: () => bumpOldest(visibleNuevas, "EN_PREPARACION"),
        disabled: visibleNuevas.length === 0,
      },
      {
        id: "bump-preparacion",
        // SCRUM-87: NO usar F6 — ver comentario en RESERVED_COMBOS
        // (use-operational-mode.ts). Chromium se lo queda para ciclar foco
        // entre paneles del browser antes de que llegue a la página.
        keys: "F8",
        label: "Marcar listo el pedido más antiguo",
        description: "Igual que tocar \"Listo\" en la primera card de En preparación",
        handler: () => bumpOldest(visibleEnPreparacion, "LISTO"),
        disabled: visibleEnPreparacion.length === 0,
      },
    ],
    [refreshOrders, cycleChannelFilter, bumpOldest, visibleNuevas, visibleEnPreparacion],
  );

  const operationalMode = useOperationalMode({ surfaceId: "kds", tenantId: tid, shortcuts });

  if (loading) {
    return <Loading />;
  }

  if (lock) {
    return (
      <ClinicalPageShell offset="shell">
        <FeatureLockedState payload={lock} />
      </ClinicalPageShell>
    );
  }

  return (
    <OperationalModeShell mode={operationalMode} className={OPERATIONAL_HOST_LAYOUT}>
      <CrudShellHeader
        title="Cocina (KDS)"
        subtitle="Pedidos en cola — pasalos a preparación y marcalos listos"
        actions={<OperationalModeToggle mode={operationalMode} />}
      />
      <ClinicalPageShell offset="none" maxWidth={false} className="!px-3 sm:!px-4 lg:!px-5 min-h-0 flex-1 space-y-4 overflow-y-auto pb-6">
        <MerchantExperienceBar screenId="kds" showTour={false} />
        <ToggleGroup
          type="single"
          value={channelFilter}
          onValueChange={(v) => v && applyChannelFilter(v as ChannelKey | "all")}
          className="justify-start"
        >
          <ToggleGroupItem value="all" size="sm" aria-label="Todos los canales">Todos</ToggleGroupItem>
          <ToggleGroupItem value="salon" size="sm" aria-label="Solo salón">Salón</ToggleGroupItem>
          <ToggleGroupItem value="retiro" size="sm" aria-label="Solo retiro">Retiro</ToggleGroupItem>
          <ToggleGroupItem value="domicilio" size="sm" aria-label="Solo delivery">Delivery</ToggleGroupItem>
        </ToggleGroup>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <KdsColumn
            title="Nuevas"
            icon={<ChefHat className="h-4 w-4" />}
            orders={visibleNuevas}
            now={now}
            pendingIds={pendingIds}
            actionLabel="Empezar preparación"
            onAction={(o) => void advanceOrder(o, "EN_PREPARACION")}
            onPrint={setPrintOrderId}
          />
          <KdsColumn
            title="En preparación"
            icon={<Flame className="h-4 w-4" />}
            orders={visibleEnPreparacion}
            now={now}
            pendingIds={pendingIds}
            actionLabel="Listo"
            onAction={(o) => void advanceOrder(o, "LISTO")}
            onPrint={setPrintOrderId}
          />
        </div>
        {orders.length === 0 && (
          <p className="py-12 text-center text-sm text-muted-foreground">No hay pedidos en cola de cocina.</p>
        )}
        {orders.length > 0 && visibleNuevas.length === 0 && visibleEnPreparacion.length === 0 && (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Ningún pedido en cola coincide con el filtro "{CHANNEL_LABELS[channelFilter as ChannelKey] ?? "Todos"}".
          </p>
        )}
      </ClinicalPageShell>

      {printOrderId && tid ? (
        <PrintOrderDialog
          open={!!printOrderId}
          onOpenChange={(v) => !v && setPrintOrderId(null)}
          orderId={printOrderId}
          type="kitchen"
          tid={tid}
        />
      ) : null}
    </OperationalModeShell>
  );
}

function KdsColumn({
  title,
  icon,
  orders,
  now,
  pendingIds,
  actionLabel,
  onAction,
  onPrint,
}: {
  title: string;
  icon: ReactNode;
  orders: KdsOrder[];
  now: number;
  pendingIds: Set<string>;
  actionLabel: string;
  onAction: (o: KdsOrder) => void;
  onPrint: (orderId: string) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        {icon} {title}
        <Badge variant="neutral">{orders.length}</Badge>
      </div>
      <div className="space-y-3">
        {orders.map((o) => {
          const mins = elapsedMinutes(o.createdAt, now);
          const busy = pendingIds.has(o.id);
          return (
            <div key={o.id} className="rounded-xl border border-border/60 bg-card p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">#{o.id.slice(-6).toUpperCase()}</span>
                  <Badge variant="outline">{CHANNEL_LABELS[channelKey(o)]}</Badge>
                </div>
                <Badge variant={urgencyVariant(mins)} className="gap-1">
                  <Clock className="h-3 w-3" /> {formatElapsed(mins)}
                </Badge>
              </div>
              <p className="mb-2 text-sm text-muted-foreground">{o.customerName}</p>
              <div className="mb-2 space-y-1 text-sm">
                {o.items.map((item) => (
                  <div key={item.id}>
                    <span className="font-medium">
                      {item.tipoVenta === "peso" ? `${item.cantidad} ${item.unidadMedida}` : `${item.cantidad}x`}{" "}
                      {item.productName}
                    </span>
                    {item.variantName ? <span className="text-muted-foreground"> — {item.variantName}</span> : null}
                    {item.selections.map((s) => (
                      <div key={s.addonProductId} className="pl-4 text-xs text-muted-foreground">
                        + {s.cantidad > 1 ? `${s.cantidad}x ` : ""}
                        {s.name}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {o.notes ? (
                <p className="mb-2 rounded-md bg-muted px-2 py-1 text-xs italic">{o.notes}</p>
              ) : null}
              <div className="flex items-center gap-2">
                <Button size="sm" className="flex-1 gap-1.5" disabled={busy} onClick={() => onAction(o)}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> {busy ? "…" : actionLabel}
                </Button>
                <Button size="sm" variant="outline" onClick={() => onPrint(o.id)} aria-label="Imprimir comanda">
                  <Printer className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
        {orders.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border/60 py-8 text-center text-xs text-muted-foreground">
            Sin pedidos
          </p>
        ) : null}
      </div>
    </div>
  );
}
